import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { calculateTrussLoad } from '../Editor/TrussSystem';
import { TRUSS_LIBRARY, JUNCTION_TYPES, CORNER_BLOCKS, BASE_PLATES, validateSpanLength, getAutoJunctionType, getFilteredCornerBlocks, getProfilesForStructure, getCompatibleBasePlates } from '../Editor/TrussData';

/**
 * Truss system configuration panel — extracted from PropertiesPanel.js.
 * Manages goalpost, box-system, grid-truss layout, load info, and section splicing.
 */
const TrussProperties = ({ selectedObject, updateSceneObjectWithMulti }) => {
  const tc = selectedObject.trussConfig;
  const updateTruss = (patch) => {
    updateSceneObjectWithMulti(selectedObject.id, { trussConfig: { ...tc, ...patch } });
  };
  const updateGoalpost = (patch) => {
    updateTruss({ goalpost: { ...(tc.goalpost || {}), ...patch } });
  };
  const updateBoxSystem = (patch) => {
    updateTruss({ boxSystem: { ...(tc.boxSystem || {}), ...patch } });
  };
  const updateCircular = (patch) => {
    updateTruss({ circular: { ...(tc.circular || {}), ...patch } });
  };
  const updateAngle = (patch) => {
    updateTruss({ angle: { ...(tc.angle || {}), ...patch } });
  };
  const loadData = calculateTrussLoad(tc);
  const profileId = tc.trussProfile || 'F34';
  const libProfile = TRUSS_LIBRARY[profileId] || TRUSS_LIBRARY['F34'];
  const structureType = tc.structureType || 'straight';

  // Auto-determined junction type based on structure
  const autoJunction = getAutoJunctionType(structureType);
  // Filtered profiles: only show profiles compatible with this structure
  const filteredProfiles = getProfilesForStructure(structureType);
  // Filtered corner blocks: by profile AND junction type
  const filteredCorners = getFilteredCornerBlocks(profileId, autoJunction);
  // Filtered base plates: by profile
  const compatBases = getCompatibleBasePlates(profileId);

  return (
  <div className="space-y-2" data-testid="truss-config-panel">
    {/* Header */}
    <div className="flex items-center gap-2 pb-1 border-b border-cyan-800/40">
      <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
      <div className="text-xs font-bold text-cyan-300 uppercase tracking-widest">Truss System</div>
      <div className="ml-auto text-[9px] text-zinc-500 capitalize">{structureType.replace('_', ' ')}</div>
    </div>

    {/* SECTION 1: Profile */}
    <div className="bg-zinc-800/60 rounded-lg p-2.5 space-y-2">
      <div className="text-[10px] font-semibold text-zinc-300 flex items-center gap-1.5">
        <svg className="w-3 h-3 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
        Truss Profile
      </div>

      <Select value={profileId} onValueChange={(v) => updateTruss({ trussProfile: v })}>
        <SelectTrigger className="h-7 text-[10px] bg-zinc-900/50 border-zinc-600" data-testid="truss-profile-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {filteredProfiles.map(p => (
            <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Profile Spec Card */}
      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 bg-zinc-900/40 rounded p-1.5 text-[9px]">
        <div className="flex justify-between"><span className="text-zinc-500">Size</span><span className="text-zinc-300">{libProfile.size_mm}mm</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">Type</span><span className="text-cyan-300 capitalize">{libProfile.geometry}</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">Tube</span><span className="text-zinc-300">{libProfile.mainTubeDiam_mm}x{libProfile.mainTubeWall_mm}mm</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">Brace</span><span className="text-zinc-300">{libProfile.braceDiam_mm || '—'}mm</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">Weight</span><span className="text-amber-300">{libProfile.weightPerMeter} kg/m</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">Material</span><span className="text-zinc-300 text-[8px]">6082 T6</span></div>
      </div>

      {/* Color */}
      <div className="flex items-center gap-2">
        <Label className="text-[10px] text-zinc-400">Color</Label>
        <input type="color" value={tc.color || '#C0C0C0'}
          onChange={(e) => updateTruss({ color: e.target.value })}
          className="w-5 h-5 rounded cursor-pointer bg-transparent border border-zinc-600"
          data-testid="truss-color" />
        <span className="text-[9px] text-zinc-500 font-mono">{tc.color || '#C0C0C0'}</span>
      </div>
    </div>

    {/* SECTION 2: Dimensions */}
    <div className="bg-zinc-800/60 rounded-lg p-2.5 space-y-2">
      <div className="text-[10px] font-semibold text-zinc-300 flex items-center gap-1.5">
        <svg className="w-3 h-3 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
        Dimensions
      </div>

      {/* === SINGLE SPAN === */}
      {structureType === 'straight' && (() => {
        const spanVal = validateSpanLength(profileId, tc.length || 3);
        return (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <Label className="text-[9px] text-zinc-500 w-20">Span (m)</Label>
            <Input type="number" min={0.5} max={20} step={0.5}
              value={tc.length || 3}
              onChange={(e) => updateTruss({ length: parseFloat(e.target.value) || 3 })}
              className="h-6 text-[10px] flex-1" data-testid="truss-length" />
          </div>
          <div className={`text-[9px] rounded px-2 py-0.5 ${
            spanVal.exact ? 'bg-green-900/30 text-green-400 border border-green-800/40' :
            spanVal.valid ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-800/40' :
            'bg-red-900/30 text-red-400 border border-red-800/40'
          }`}>
            {spanVal.exact ? 'Standard size' : spanVal.message}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Switch checked={tc.showBasePlates || false}
              onCheckedChange={(v) => updateTruss({ showBasePlates: v })}
              data-testid="truss-straight-base-plates" />
            <Label className="text-[9px] text-zinc-400">End Base Plates</Label>
          </div>
        </div>
        );
      })()}

      {/* === GOAL POST === */}
      {structureType === 'goalpost' && (() => {
        const gp = tc.goalpost || {};
        return (
        <div className="space-y-1.5">
          <div className="grid grid-cols-3 gap-1">
            <div className="space-y-0.5">
              <Label className="text-[8px] text-zinc-500">Left H (m)</Label>
              <Input type="number" min={2} max={20} step={0.5}
                value={gp.heightLeft || 5}
                onChange={(e) => updateGoalpost({ heightLeft: parseFloat(e.target.value) || 5 })}
                className="h-6 text-[10px]" data-testid="truss-gp-height-left" />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[8px] text-zinc-500">Right H (m)</Label>
              <Input type="number" min={2} max={20} step={0.5}
                value={gp.heightRight || 5}
                onChange={(e) => updateGoalpost({ heightRight: parseFloat(e.target.value) || 5 })}
                className="h-6 text-[10px]" data-testid="truss-gp-height-right" />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[8px] text-zinc-500">Top W (m)</Label>
              <Input type="number" min={2} max={30} step={0.5}
                value={gp.topWidth || 6}
                onChange={(e) => updateGoalpost({ topWidth: parseFloat(e.target.value) || 6 })}
                className="h-6 text-[10px]" data-testid="truss-gp-top-width" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={tc.showBasePlates !== false}
              onCheckedChange={(v) => updateTruss({ showBasePlates: v })}
              data-testid="truss-gp-base-plates" />
            <Label className="text-[9px] text-zinc-400">Base Plates</Label>
          </div>
        </div>
        );
      })()}

      {/* === BOX SYSTEM === */}
      {structureType === 'box_system' && (() => {
        const bs = tc.boxSystem || {};
        return (
        <div className="space-y-1.5">
          <div className="grid grid-cols-3 gap-1">
            <div className="space-y-0.5">
              <Label className="text-[8px] text-zinc-500">Width (m)</Label>
              <Input type="number" min={2} max={40} step={1}
                value={bs.width || 8}
                onChange={(e) => updateBoxSystem({ width: parseFloat(e.target.value) || 8 })}
                className="h-6 text-[10px]" data-testid="truss-bs-width" />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[8px] text-zinc-500">Depth (m)</Label>
              <Input type="number" min={2} max={40} step={1}
                value={bs.depth || 6}
                onChange={(e) => updateBoxSystem({ depth: parseFloat(e.target.value) || 6 })}
                className="h-6 text-[10px]" data-testid="truss-bs-depth" />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[8px] text-zinc-500">Height (m)</Label>
              <Input type="number" min={2} max={20} step={0.5}
                value={bs.height || 6}
                onChange={(e) => updateBoxSystem({ height: parseFloat(e.target.value) || 6 })}
                className="h-6 text-[10px]" data-testid="truss-bs-height" />
            </div>
          </div>

          {/* Pillar System */}
          <div className="grid grid-cols-2 gap-1">
            <Select value={bs.pillarMode || 'auto'} onValueChange={(v) => updateBoxSystem({ pillarMode: v })}>
              <SelectTrigger className="h-6 text-[9px]" data-testid="truss-bs-pillar-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto Pillars</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
              </SelectContent>
            </Select>
            {bs.pillarMode === 'manual' ? (
              <Select value={String(bs.pillarCount || 4)} onValueChange={(v) => updateBoxSystem({ pillarCount: parseInt(v) })}>
                <SelectTrigger className="h-6 text-[9px]" data-testid="truss-bs-pillar-count">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">4 Pillars</SelectItem>
                  <SelectItem value="6">6 Pillars</SelectItem>
                  <SelectItem value="8">8 Pillars</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div className="flex items-center text-[9px] text-zinc-500 pl-2">
                {(bs.width || 8) > 20 ? '8' : (bs.width || 8) > 10 ? '6' : '4'} pillars
              </div>
            )}
          </div>

          {/* Lift System */}
          <Select value={bs.liftMode || 'manual'} onValueChange={(v) => updateBoxSystem({ liftMode: v })}>
            <SelectTrigger className="h-6 text-[9px]" data-testid="truss-bs-lift-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">Static (Manual)</SelectItem>
              <SelectItem value="motor">Motorized</SelectItem>
            </SelectContent>
          </Select>
          {bs.liftMode === 'motor' && (
            <div className="flex items-center gap-1">
              <Input type="number" min={0} max={20} step={0.5}
                value={bs.liftHeight || 0}
                onChange={(e) => updateBoxSystem({ liftHeight: parseFloat(e.target.value) || 0 })}
                className="h-6 text-[9px] w-16" data-testid="truss-bs-lift-height" />
              <input type="range" min={0} max={20} step={0.5}
                value={bs.liftHeight || 0}
                onChange={(e) => updateBoxSystem({ liftHeight: parseFloat(e.target.value) })}
                className="flex-1 h-1.5 accent-cyan-500" />
            </div>
          )}

          <div className="flex items-center gap-2">
            <Label className="text-[8px] text-zinc-500 w-14">Shift X</Label>
            <Input type="number" min={-20} max={20} step={0.5}
              value={bs.shiftX || 0}
              onChange={(e) => updateBoxSystem({ shiftX: parseFloat(e.target.value) || 0 })}
              className="h-6 text-[9px] flex-1" data-testid="truss-bs-shift-x" />
          </div>

          <div className="grid grid-cols-1 gap-x-2 gap-y-1">
            <div className="flex items-center gap-1.5">
              <Switch checked={tc.showBasePlates !== false}
                onCheckedChange={(v) => updateTruss({ showBasePlates: v })}
                data-testid="truss-bs-base-plates" />
              <Label className="text-[9px] text-zinc-400">Base Plates</Label>
            </div>
          </div>

          {/* Grid Truss Section - individually configurable top trusses */}
          <div className="space-y-1.5 pt-1 border-t border-zinc-700/50" data-testid="grid-truss-section">
            <div className="flex items-center justify-between">
              <span className="text-[9px] text-zinc-300 font-semibold">Grid Trusses</span>
              <button
                onClick={() => {
                  const existing = bs.gridTrusses || [];
                  updateBoxSystem({
                    gridTrusses: [...existing, {
                      profile: tc.trussProfile || 'F34',
                      position: 0,
                      rotation: 0,
                      length: null,
                      attached: true,
                      offsetX: 0,
                    }]
                  });
                }}
                className="text-[8px] px-1.5 py-0.5 bg-cyan-700/40 hover:bg-cyan-600/50 text-cyan-300 rounded transition-colors"
                data-testid="add-grid-truss-btn"
              >
                + Add Truss
              </button>
            </div>

            {(bs.gridTrusses || []).map((gt, idx) => {
              const rotDeg = gt.rotation || 0;
              let defaultLen = bs.width || 8;
              if (Math.abs(rotDeg - 90) < 1 || Math.abs(rotDeg - 270) < 1) defaultLen = bs.depth || 6;
              const effectiveLen = gt.length || defaultLen;
              return (
              <div key={gt.id || `grid-${idx}-${gt.rotation || 0}`} className="bg-zinc-900/50 rounded p-1.5 space-y-1 border border-zinc-700/40" data-testid={`grid-truss-${idx}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[8px] text-zinc-400 font-semibold">Grid #{idx + 1}</span>
                  <button
                    onClick={() => {
                      const updated = [...(bs.gridTrusses || [])];
                      updated.splice(idx, 1);
                      updateBoxSystem({ gridTrusses: updated.length > 0 ? updated : undefined });
                    }}
                    className="text-[8px] text-red-400 hover:text-red-300 px-1"
                    data-testid={`remove-grid-truss-${idx}`}
                  >
                    Remove
                  </button>
                </div>
                {/* Profile + Mode */}
                <div className="grid grid-cols-2 gap-1">
                  <div className="space-y-0.5">
                    <Label className="text-[7px] text-zinc-600">Profile</Label>
                    <select
                      value={gt.profile || tc.trussProfile || 'F34'}
                      onChange={(e) => {
                        const updated = [...(bs.gridTrusses || [])];
                        updated[idx] = { ...updated[idx], profile: e.target.value };
                        updateBoxSystem({ gridTrusses: updated });
                      }}
                      className="w-full h-5 text-[8px] bg-zinc-900/60 border border-zinc-700 rounded px-1 text-zinc-300 outline-none"
                      data-testid={`grid-truss-profile-${idx}`}
                    >
                      {filteredProfiles.map(p => (
                        <option key={p.id} value={p.id}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-[7px] text-zinc-600">Mode</Label>
                    <select
                      value={gt.attached !== false ? 'attached' : 'ontop'}
                      onChange={(e) => {
                        const updated = [...(bs.gridTrusses || [])];
                        updated[idx] = { ...updated[idx], attached: e.target.value === 'attached' };
                        updateBoxSystem({ gridTrusses: updated });
                      }}
                      className="w-full h-5 text-[8px] bg-zinc-900/60 border border-zinc-700 rounded px-1 text-zinc-300 outline-none"
                      data-testid={`grid-truss-mode-${idx}`}
                    >
                      <option value="attached">Attached</option>
                      <option value="ontop">On Top</option>
                    </select>
                  </div>
                </div>
                {/* Position + Rotation */}
                <div className="grid grid-cols-2 gap-1">
                  <div className="space-y-0.5">
                    <Label className="text-[7px] text-zinc-600">Position Z (m)</Label>
                    <Input type="number" step={0.1}
                      value={gt.position ?? 0}
                      onChange={(e) => {
                        const updated = [...(bs.gridTrusses || [])];
                        updated[idx] = { ...updated[idx], position: parseFloat(e.target.value) || 0 };
                        updateBoxSystem({ gridTrusses: updated });
                      }}
                      className="h-5 text-[8px]"
                      data-testid={`grid-truss-position-${idx}`}
                    />
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-[7px] text-zinc-600">Rotation (deg)</Label>
                    <Input type="number" min={0} max={360} step={15}
                      value={gt.rotation ?? 0}
                      onChange={(e) => {
                        const updated = [...(bs.gridTrusses || [])];
                        updated[idx] = { ...updated[idx], rotation: parseFloat(e.target.value) || 0 };
                        updateBoxSystem({ gridTrusses: updated });
                      }}
                      className="h-5 text-[8px]"
                      data-testid={`grid-truss-rotation-${idx}`}
                    />
                  </div>
                </div>
                {/* Length + Shift X */}
                <div className="grid grid-cols-2 gap-1">
                  <div className="space-y-0.5">
                    <Label className="text-[7px] text-zinc-600">Length (m)</Label>
                    <Input type="number" min={0.5} max={30} step={0.5}
                      value={effectiveLen}
                      onChange={(e) => {
                        const updated = [...(bs.gridTrusses || [])];
                        updated[idx] = { ...updated[idx], length: parseFloat(e.target.value) || null };
                        updateBoxSystem({ gridTrusses: updated });
                      }}
                      className="h-5 text-[8px]"
                      data-testid={`grid-truss-length-${idx}`}
                    />
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-[7px] text-zinc-600">Shift X (m)</Label>
                    <Input type="number" step={0.1}
                      value={gt.offsetX ?? 0}
                      onChange={(e) => {
                        const updated = [...(bs.gridTrusses || [])];
                        const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                        updated[idx] = { ...updated[idx], offsetX: isNaN(val) ? 0 : val };
                        updateBoxSystem({ gridTrusses: updated });
                      }}
                      className="h-5 text-[8px]"
                      data-testid={`grid-truss-offset-${idx}`}
                    />
                  </div>
                </div>
                {/* Shift Y (height offset) */}
                <div className="grid grid-cols-2 gap-1">
                  <div className="space-y-0.5">
                    <Label className="text-[7px] text-zinc-600">Shift Y (m)</Label>
                    <Input type="number" step={0.1}
                      value={gt.offsetY ?? 0}
                      onChange={(e) => {
                        const updated = [...(bs.gridTrusses || [])];
                        const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                        updated[idx] = { ...updated[idx], offsetY: isNaN(val) ? 0 : val };
                        updateBoxSystem({ gridTrusses: updated });
                      }}
                      className="h-5 text-[8px]"
                      data-testid={`grid-truss-offsety-${idx}`}
                    />
                  </div>
                </div>
                <div className="text-[7px] text-zinc-600">
                  {gt.attached !== false ? 'Attached to frame' : 'On top of frame'}
                  {' · '}{effectiveLen}m · {rotDeg}°
                  {(gt.offsetY != null && gt.offsetY !== 0) ? ` · Y+${gt.offsetY}m` : ''}
                </div>
              </div>
              );
            })}

            {(!bs.gridTrusses || bs.gridTrusses.length === 0) && (
              <div className="text-[7px] text-zinc-600 text-center py-1">No grid trusses added</div>
            )}
          </div>
        </div>
        );
      })()}

      {/* === CIRCULAR === */}
      {structureType === 'circular' && (() => {
        const circ = tc.circular || {};
        return (
        <div className="space-y-1.5">
          <div className="grid grid-cols-3 gap-1">
            <div className="space-y-0.5">
              <Label className="text-[8px] text-zinc-500">Radius (m)</Label>
              <Input type="number" min={1} max={20} step={0.5}
                value={circ.radius || 3}
                onChange={(e) => updateCircular({ radius: parseFloat(e.target.value) || 3 })}
                className="h-6 text-[10px]" data-testid="truss-circ-radius" />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[8px] text-zinc-500">Segments</Label>
              <Input type="number" min={4} max={36} step={1}
                value={circ.segments || 12}
                onChange={(e) => updateCircular({ segments: parseInt(e.target.value) || 12 })}
                className="h-6 text-[10px]" data-testid="truss-circ-segments" />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[8px] text-zinc-500">Arc (deg)</Label>
              <Input type="number" min={30} max={360} step={15}
                value={circ.arcAngle || 360}
                onChange={(e) => updateCircular({ arcAngle: parseInt(e.target.value) || 360 })}
                className="h-6 text-[10px]" data-testid="truss-circ-arc" />
            </div>
          </div>
        </div>
        );
      })()}

      {/* === ANGLE === */}
      {structureType === 'angle' && (() => {
        const ang = tc.angle || {};
        return (
        <div className="space-y-1.5">
          <div className="grid grid-cols-3 gap-1">
            <div className="space-y-0.5">
              <Label className="text-[8px] text-zinc-500">Len 1 (m)</Label>
              <Input type="number" min={0.5} max={10} step={0.5}
                value={ang.length1 || 2}
                onChange={(e) => updateAngle({ length1: parseFloat(e.target.value) || 2 })}
                className="h-6 text-[10px]" data-testid="truss-ang-len1" />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[8px] text-zinc-500">Len 2 (m)</Label>
              <Input type="number" min={0.5} max={10} step={0.5}
                value={ang.length2 || 2}
                onChange={(e) => updateAngle({ length2: parseFloat(e.target.value) || 2 })}
                className="h-6 text-[10px]" data-testid="truss-ang-len2" />
            </div>
            <div className="space-y-0.5">
              <Label className="text-[8px] text-zinc-500">Angle</Label>
              <Input type="number" min={15} max={180} step={5}
                value={ang.angle || 90}
                onChange={(e) => updateAngle({ angle: parseInt(e.target.value) || 90 })}
                className="h-6 text-[10px]" data-testid="truss-ang-angle" />
            </div>
          </div>
          <Select value={ang.direction || 'horizontal'} onValueChange={(v) => updateAngle({ direction: v })}>
            <SelectTrigger className="h-6 text-[9px]" data-testid="truss-ang-direction">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="horizontal">Horizontal Bend</SelectItem>
              <SelectItem value="vertical">Vertical Bend</SelectItem>
            </SelectContent>
          </Select>
        </div>
        );
      })()}
    </div>

    {/* SECTION 3: Hardware (Junction auto-set, Corner Blocks, Base Plates) */}
    <div className="bg-zinc-800/60 rounded-lg p-2.5 space-y-2">
      <div className="text-[10px] font-semibold text-zinc-300 flex items-center gap-1.5">
        <svg className="w-3 h-3 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" /></svg>
        Hardware & Connectors
      </div>

      {/* Auto Junction Type (read-only info) */}
      <div className="flex items-center justify-between bg-zinc-900/40 rounded px-2 py-1">
        <span className="text-[9px] text-zinc-500">Junction Type</span>
        <span className="text-[9px] text-cyan-300 font-semibold" data-testid="truss-junction-type">
          {JUNCTION_TYPES.find(j => j.id === autoJunction)?.label || autoJunction}
        </span>
      </div>

      {/* Corner Block Catalog (filtered by profile + junction) */}
      {filteredCorners.length > 0 && (
        <div className="space-y-1">
          <Label className="text-[9px] text-zinc-500">Corner Block</Label>
          <Select value={tc.cornerBlockModel || 'auto'} onValueChange={(v) => updateTruss({ cornerBlockModel: v === 'auto' ? undefined : v })}>
            <SelectTrigger className="h-6 text-[9px] bg-zinc-900/50" data-testid="truss-corner-block">
              <SelectValue placeholder="Auto" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto (Generic)</SelectItem>
              {filteredCorners.map(cb => (
                <SelectItem key={cb.id} value={cb.id}>
                  {cb.id} — {cb.description} ({cb.weight}kg)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {tc.cornerBlockModel && CORNER_BLOCKS[tc.cornerBlockModel] && (
            <div className="text-[8px] text-zinc-500 bg-zinc-900/40 rounded px-1.5 py-0.5">
              {CORNER_BLOCKS[tc.cornerBlockModel].description} | {CORNER_BLOCKS[tc.cornerBlockModel].weight}kg | {CORNER_BLOCKS[tc.cornerBlockModel].size_mm}mm
            </div>
          )}
        </div>
      )}

      {/* Base Plate Catalog (filtered by profile) */}
      {compatBases.length > 0 && (
        <div className="space-y-1">
          <Label className="text-[9px] text-zinc-500">Base Plate</Label>
          <Select value={tc.basePlateModel || 'auto'} onValueChange={(v) => updateTruss({ basePlateModel: v === 'auto' ? undefined : v })}>
            <SelectTrigger className="h-6 text-[9px] bg-zinc-900/50" data-testid="truss-base-plate">
              <SelectValue placeholder="Auto" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto (Default)</SelectItem>
              {compatBases.map(bp => (
                <SelectItem key={bp.id} value={bp.id}>
                  {bp.id} — {bp.size_mm}mm ({bp.maxLoad_kg}kg)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {tc.basePlateModel && BASE_PLATES[tc.basePlateModel] && (
            <div className="text-[8px] text-zinc-500 bg-zinc-900/40 rounded px-1.5 py-0.5">
              {BASE_PLATES[tc.basePlateModel].description} | Max: {BASE_PLATES[tc.basePlateModel].maxLoad_kg}kg
              {BASE_PLATES[tc.basePlateModel].hasOutriggers && ' | + Outriggers'}
            </div>
          )}
        </div>
      )}
    </div>

    {/* SECTION 4: Support Legs (Goalpost & Box) */}
    {(structureType === 'box_system' || structureType === 'goalpost') && (
    <div className="bg-zinc-800/60 rounded-lg p-2.5 space-y-2">
      <div className="text-[10px] font-semibold text-zinc-300 flex items-center gap-1.5">
        <svg className="w-3 h-3 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
        Support Legs (4-Side)
      </div>
      <div className="flex items-center gap-2">
        <Switch
          checked={tc.showSupportLegs !== false}
          onCheckedChange={(v) => updateTruss({ showSupportLegs: v })}
          data-testid="truss-support-legs" />
        <Label className="text-[9px] text-zinc-400">Enable Support Legs</Label>
      </div>
      {tc.showSupportLegs !== false && (
        <div className="text-[8px] text-zinc-500 bg-zinc-900/40 rounded px-2 py-1">
          Auto-sized: {libProfile.size_mm}mm profile, spread {Math.round(libProfile.size_mm * 4)}mm, height {Math.round(libProfile.size_mm * 3)}mm
        </div>
      )}
    </div>
    )}

    {/* SECTION 5: Section Breakdown & Manual Override */}
    <div className="bg-zinc-800/60 rounded-lg p-2.5 space-y-2">
      <div className="text-[10px] font-semibold text-zinc-300 flex items-center gap-1.5">
        <svg className="w-3 h-3 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
        Section Breakdown
      </div>

      {/* Section counts summary */}
      {loadData.sectionCounts && Object.keys(loadData.sectionCounts).length > 0 && (
        <div className="bg-zinc-900/40 rounded p-1.5 space-y-1">
          <div className="text-[8px] text-cyan-400 font-semibold">Truss Sections Used:</div>
          <div className="flex flex-wrap gap-1">
            {Object.entries(loadData.sectionCounts).sort((a, b) => parseFloat(b[0]) - parseFloat(a[0])).map(([size, count]) => (
              <div key={size} className="bg-zinc-800 rounded px-1.5 py-0.5 text-[9px]">
                <span className="text-cyan-300 font-bold">{count}x</span>
                <span className="text-zinc-400 ml-0.5">{size}</span>
              </div>
            ))}
          </div>
          <div className="text-[8px] text-zinc-600">
            Total: {loadData.totalSections} sections | {loadData.totalJoints} joints | {loadData.cornerBlockCount} corner blocks
          </div>
        </div>
      )}

      {/* Hardware BOM (Bill of Materials) */}
      {loadData.totalJoints > 0 && (
        <div className="bg-zinc-900/40 rounded p-1.5 space-y-1" data-testid="truss-hardware-bom">
          <div className="text-[8px] text-amber-400 font-semibold">Joint Accessories (BOM)</div>
          <div className="grid grid-cols-3 gap-1">
            <div className="bg-zinc-800 rounded px-1.5 py-1 text-center" data-testid="truss-bom-pins">
              <div className="text-[10px] text-cyan-300 font-bold">{loadData.totalCouplerPins}</div>
              <div className="text-[7px] text-zinc-500">Coupler Pins</div>
            </div>
            <div className="bg-zinc-800 rounded px-1.5 py-1 text-center" data-testid="truss-bom-spacers">
              <div className="text-[10px] text-cyan-300 font-bold">{loadData.totalSpacers}</div>
              <div className="text-[7px] text-zinc-500">Spacers</div>
            </div>
            <div className="bg-zinc-800 rounded px-1.5 py-1 text-center" data-testid="truss-bom-clips">
              <div className="text-[10px] text-cyan-300 font-bold">{loadData.totalClips}</div>
              <div className="text-[7px] text-zinc-500">R-Clips</div>
            </div>
          </div>
        </div>
      )}

      {/* Per-span breakdowns with profile selector and section controls */}
      {loadData.sectionBreakdowns && Object.entries(loadData.sectionBreakdowns).map(([key, bd]) => {
        const overrides = (tc.sectionOverrides || {})[key];
        const spanProfileId = bd.profileId || profileId;
        const isCustomProfile = spanProfileId !== profileId;
        const spanLib = TRUSS_LIBRARY[spanProfileId] || TRUSS_LIBRARY[profileId];
        const currentSections = overrides || bd.sections;
        const currentTotal = Math.round(currentSections.reduce((a, b) => a + b, 0) * 100) / 100;
        const targetLen = bd.length;
        const isValid = Math.abs(currentTotal - targetLen) < 0.02;
        const availLengths = spanLib?.availableLengths || [0.5, 1, 2, 3, 4];
        return (
        <div key={key} className="space-y-1 border-t border-zinc-700/50 pt-1">
          <div className="flex items-center justify-between cursor-pointer group"
            onClick={() => updateTruss({ highlightedSpan: tc.highlightedSpan === key ? undefined : key })}
            data-testid={`span-highlight-${key}`}
          >
            <span className={`text-[8px] capitalize font-semibold transition-colors ${tc.highlightedSpan === key ? 'text-yellow-400' : 'text-zinc-400 group-hover:text-zinc-200'}`}>
              {tc.highlightedSpan === key ? '● ' : '○ '}{key.replace(/([A-Z])/g, ' $1').trim()}
            </span>
            <span className={`text-[8px] ${isValid ? 'text-green-400' : 'text-red-400'}`}>{currentTotal}m / {targetLen}m</span>
          </div>
          {/* Per-span profile selector */}
          <div className="flex items-center gap-1" data-testid={`span-profile-${key}`}>
            <span className="text-[7px] text-zinc-600 shrink-0">Profile:</span>
            <select
              value={spanProfileId}
              onChange={(e) => {
                const v = e.target.value;
                const newSpanProfiles = { ...(tc.spanProfiles || {}) };
                if (v === profileId) {
                  delete newSpanProfiles[key];
                } else {
                  newSpanProfiles[key] = v;
                }
                // Clear section overrides for this span when profile changes
                const newOverrides = { ...(tc.sectionOverrides || {}) };
                delete newOverrides[key];
                updateTruss({
                  spanProfiles: Object.keys(newSpanProfiles).length > 0 ? newSpanProfiles : undefined,
                  sectionOverrides: Object.keys(newOverrides).length > 0 ? newOverrides : undefined
                });
              }}
              className="flex-1 h-5 text-[8px] bg-zinc-900/60 border border-zinc-700 rounded px-1 text-zinc-300 outline-none focus:border-cyan-600"
              data-testid={`span-profile-select-${key}`}
            >
              {filteredProfiles.map(p => (
                <option key={p.id} value={p.id}>{p.id === profileId ? `${p.label} (main)` : p.label}</option>
              ))}
            </select>
            {isCustomProfile && (
              <span className="text-[7px] text-amber-400 shrink-0" title="Different from main profile">⚠</span>
            )}
          </div>
          {/* Current sections as removable tags */}
          <div className="flex flex-wrap gap-0.5">
            {currentSections.map((sec, idx) => (
              <button key={`${sec}-${idx}`}
                className="text-[8px] px-1.5 py-0.5 rounded bg-cyan-900/40 border border-cyan-700/50 text-cyan-300 hover:bg-red-900/40 hover:border-red-600 hover:text-red-300 group"
                title="Click to remove"
                onClick={() => {
                  const newSections = [...currentSections];
                  newSections.splice(idx, 1);
                  const newOverrides = { ...(tc.sectionOverrides || {}) };
                  if (newSections.length === 0) {
                    delete newOverrides[key];
                  } else {
                    newOverrides[key] = newSections;
                  }
                  updateTruss({ sectionOverrides: Object.keys(newOverrides).length > 0 ? newOverrides : undefined });
                }}
                data-testid={`truss-sec-remove-${key}-${idx}`}
              >
                {sec}m <span className="text-zinc-600 group-hover:text-red-400 ml-0.5">x</span>
              </button>
            ))}
          </div>
          {/* Add section buttons */}
          <div className="flex gap-0.5 items-center">
            <span className="text-[7px] text-zinc-600 mr-0.5">Add:</span>
            {availLengths.map(size => {
              const newTotal = currentTotal + size;
              const wouldExceed = newTotal > targetLen + 0.02;
              return (
                <button key={size}
                  disabled={wouldExceed}
                  className={`text-[7px] px-1 py-0.5 rounded border ${
                    wouldExceed ? 'border-zinc-800 text-zinc-700 cursor-not-allowed' : 'border-zinc-600 text-zinc-400 hover:border-cyan-500 hover:text-cyan-300'
                  }`}
                  onClick={() => {
                    if (wouldExceed) return;
                    const newSections = [...currentSections, size];
                    const newOverrides = { ...(tc.sectionOverrides || {}) };
                    newOverrides[key] = newSections;
                    updateTruss({ sectionOverrides: newOverrides });
                  }}
                  data-testid={`truss-sec-add-${key}-${size}m`}
                >+{size}m</button>
              );
            })}
            {/* Reset */}
            {overrides && (
              <button
                className="text-[7px] px-1 py-0.5 rounded border border-red-800 text-red-400 hover:bg-red-900/30 ml-1"
                onClick={() => {
                  const newOverrides = { ...(tc.sectionOverrides || {}) };
                  delete newOverrides[key];
                  updateTruss({ sectionOverrides: Object.keys(newOverrides).length > 0 ? newOverrides : undefined });
                }}
                data-testid={`truss-sec-reset-${key}`}
              >Auto</button>
            )}
          </div>
        </div>
        );
      })}

      {/* Profile mismatch warning at corners */}
      {(() => {
        const sp = tc.spanProfiles || {};
        const hasCustom = Object.keys(sp).length > 0;
        if (!hasCustom) return null;
        const spanKeys = Object.keys(loadData.sectionBreakdowns || {});
        const profiles = spanKeys.map(k => sp[k] || profileId);
        const uniqueProfiles = [...new Set(profiles)];
        if (uniqueProfiles.length <= 1) return null;
        return (
          <div className="bg-amber-900/20 border border-amber-700/40 rounded p-1.5 mt-1" data-testid="profile-mismatch-warning">
            <div className="text-[8px] text-amber-400 font-semibold flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
              Mixed Profile Warning
            </div>
            <div className="text-[7px] text-amber-300/70 mt-0.5">
              Different truss profiles at junction corners may require adapter sleeves. Verify corner block compatibility for: {uniqueProfiles.join(', ')}
            </div>
          </div>
        );
      })()}
    </div>

    {/* SECTION 6: Load Analysis */}
    <div className="bg-zinc-800/60 rounded-lg p-2.5 space-y-2">
      <div className="text-[10px] font-semibold text-zinc-300 flex items-center gap-1.5">
        <svg className="w-3 h-3 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
        Load Analysis
      </div>

      <div className="flex items-center gap-2">
        <Label className="text-[9px] text-zinc-500 w-20">Applied (kg)</Label>
        <Input type="number" min={0} max={10000} step={10}
          value={tc.appliedLoad || 0}
          onChange={(e) => updateTruss({ appliedLoad: parseFloat(e.target.value) || 0 })}
          className="h-6 text-[10px] flex-1" data-testid="truss-applied-load" />
      </div>

      {(tc.appliedLoad || 0) > 0 && (
        <div className={`rounded px-2 py-1 text-[10px] font-bold text-center ${
          loadData.loadStatusColor === 'green' ? 'bg-green-900/40 text-green-400 border border-green-700/50' :
          loadData.loadStatusColor === 'yellow' ? 'bg-yellow-900/40 text-yellow-400 border border-yellow-700/50' :
          'bg-red-900/40 text-red-400 border border-red-700/50'
        }`} data-testid="truss-load-status">
          {loadData.loadStatusText} — {Math.round(loadData.loadRatio * 100)}% capacity
          <div className="w-full bg-zinc-800 rounded-full h-1 mt-1">
            <div className={`h-1 rounded-full transition-all ${
              loadData.loadStatusColor === 'green' ? 'bg-green-400' :
              loadData.loadStatusColor === 'yellow' ? 'bg-yellow-400' : 'bg-red-400'
            }`} style={{ width: `${Math.min(100, Math.round(loadData.loadRatio * 100))}%` }}></div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[9px]">
        <span className="text-zinc-500">Profile:</span>
        <span className="text-zinc-300">{loadData.profileLabel}</span>
        <span className="text-zinc-500">Structure:</span>
        <span className="text-zinc-300 capitalize">{loadData.structureType.replace('_', ' ')}</span>
        <span className="text-zinc-500">Total Length:</span>
        <span className="text-zinc-300">{loadData.totalLength} m</span>
        <span className="text-zinc-500">Critical Span:</span>
        <span className="text-zinc-300">{loadData.mainSpan} m</span>
        <span className="text-zinc-500">Total Weight:</span>
        <span className="text-amber-300">{loadData.totalWeight} kg</span>
      </div>

      <div className="grid grid-cols-3 gap-1">
        <div className="bg-zinc-900/60 rounded p-1.5 text-center">
          <div className="text-[8px] text-zinc-500">Max UDL</div>
          <div className="text-[11px] font-bold text-cyan-300">{loadData.maxUDL}</div>
          <div className="text-[7px] text-zinc-600">kg</div>
        </div>
        <div className="bg-zinc-900/60 rounded p-1.5 text-center">
          <div className="text-[8px] text-zinc-500">Point Load</div>
          <div className="text-[11px] font-bold text-cyan-300">{loadData.maxPointLoad}</div>
          <div className="text-[7px] text-zinc-600">kg</div>
        </div>
        <div className="bg-zinc-900/60 rounded p-1.5 text-center">
          <div className="text-[8px] text-zinc-500">Deflection</div>
          <div className="text-[11px] font-bold text-orange-300">{loadData.deflection}</div>
          <div className="text-[7px] text-zinc-600">mm</div>
        </div>
      </div>

      {loadData.loadInterpolated && (
        <div className="text-[8px] text-zinc-600">* Interpolated for {loadData.mainSpan}m span</div>
      )}
      {loadData.exceedsMaxSpan && (
        <div className="text-[9px] text-red-400 bg-red-900/20 rounded px-2 py-0.5">
          Exceeds max rated span for this profile
        </div>
      )}
    </div>
  </div>
  );
};

export default TrussProperties;
